import React, { ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Typography } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import {
  Button,
  DeleteButton,
  PhoneEntryBox,
  SaveCancelButtons,
  formatPhoneNumber,
} from '~components/common'
import Table, { TableHeader } from '~components/common/Table'
import { lightTheme } from '~createTheme'
import { RootState } from '~redux/rootReducer'
import { ContactId, PhoneNumber0 } from '~types'

type PhoneSettingsTableProps = {
  contactId: ContactId
  onSave?: () => void
  inputPhones?: PhoneNumber0[]
}

const PhoneSettingsTable = ({
  contactId,
  onSave,
  inputPhones,
}: PhoneSettingsTableProps): ReactElement => {
  const user = useSelector((state: RootState) => state?.auth.user)
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber0[]>(inputPhones || [])
  const [showPhoneNumberInput, setShowPhoneNumberInput] = useState(false)
  const [phoneNumberInput, setPhoneNumberInput] = useState('')
  const [phoneIsValid, setPhoneIsValid] = useState(false)

  const listPhoneNumbers = async (): Promise<void> => {
    try {
      if (!user) {
        return
      }
      const response = await apiZodios.contacts_listPhoneNumbers({
        user_id: user?.id,
        contact_ids: [contactId],
      })
      setPhoneNumbers(response.phone_numbers as PhoneNumber0[])
    } catch (_err) {}
  }

  const addPhoneNumber = async (): Promise<void> => {
    try {
      if (!user) {
        return
      }
      const optimisticId = Date.now().toString()
      const optimisticPhone: PhoneNumber0 = {
        phone_number_id: optimisticId,
        contact_id: contactId,
        phone_number: phoneNumberInput,
      }
      setPhoneNumbers((prev) => [...prev, optimisticPhone])
      setShowPhoneNumberInput(false)
      setPhoneNumberInput('')
      const _response = await apiZodios.contacts_addPhoneNumber({
        user_id: user?.id,
        contact_id: contactId,
        phone_number: phoneNumberInput,
      })
      listPhoneNumbers()
    } catch (_err) {
      listPhoneNumbers()
    }
  }

  const removePhoneNumber = async (phone: PhoneNumber0): Promise<void> => {
    try {
      if (!user) {
        return
      }
      setPhoneNumbers((prev) => prev.filter((p) => p.phone_number_id !== phone.phone_number_id))
      const _response = await apiZodios.contacts_removePhoneNumber({
        user_id: user?.id,
        phone_number_id: phone.phone_number_id,
        contact_id: contactId,
      })
      listPhoneNumbers()
    } catch (_err) {
      listPhoneNumbers()
    }
  }

  const handlePhoneChange = (newNumber: string, newIsValid: boolean): void => {
    setPhoneNumberInput(newNumber)
    setPhoneIsValid(newIsValid)
  }

  const cancelAddPhoneNumber = (): void => {
    setShowPhoneNumberInput(false)
    setPhoneNumberInput('')
  }

  useEffect(() => {
    listPhoneNumbers()
  }, [contactId, user])

  const PhoneRowContent: React.FC<{ row: PhoneNumber0 }> = ({ row }) => {
    return (
      <Box display='flex' alignItems='center' justifyContent='space-between' width='100%' mb={1}>
        <Typography variant='h4' sx={{ mr: 2, flexShrink: 0 }}>
          {formatPhoneNumber(row.phone_number)}
        </Typography>
        <DeleteButton item={row} onDelete={removePhoneNumber} itemName={row.phone_number} />
      </Box>
    )
  }

  const phoneTable: TableHeader<PhoneNumber0>[] = [
    {
      id: 'phone_number',
      label: 'Phone Information',
      render: (row) => <PhoneRowContent row={row} />,
    },
  ]

  return (
    <Box display='flex' flexDirection='column' width='600px' padding={2}>
      <Table headers={phoneTable} data={phoneNumbers} loading={false} ignoreMaxHeight={true} />
      <Box display='flex' flexDirection='column' width='100%'>
        {showPhoneNumberInput ? (
          <>
            <Box display='flex' justifyContent='center' width='100%' mb={2} mt={2}>
              <Box width='100%' maxWidth='400px'>
                <PhoneEntryBox inputPhoneState={phoneNumberInput} onChange={handlePhoneChange} />
              </Box>
            </Box>
            <Box display='flex' justifyContent='flex-end'>
              <SaveCancelButtons
                onSave={addPhoneNumber}
                onCancel={cancelAddPhoneNumber}
                isSaveDisabled={!phoneIsValid}
              />
            </Box>
          </>
        ) : (
          <Box display='flex' justifyContent='flex-end'>
            <Button
              variant='outlined'
              color='secondary'
              sx={{ m: 2 }}
              onClick={() => setShowPhoneNumberInput(true)}
            >
              Add Phone
            </Button>
            {onSave && (
              <Button
                variant='contained'
                color='secondary'
                style={{ margin: lightTheme.spacing(2) }}
                onClick={onSave}
              >
                Save
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default PhoneSettingsTable
